import { useState, useEffect, useCallback } from 'react';
import { proyectoApi } from '../api/proyectoApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

/**
 * Hook personalizado para gestión de proyectos
 * Proporciona estado y funciones para operaciones CRUD de proyectos
 */
export const useProyectos = () => {
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Obtiene todos los proyectos
   */
  const fetchProyectos = useCallback(async (id = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await proyectoApi.getAll(id);
      setProyectos(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar proyectos');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene un proyecto específico por ID
   */
  const fetchProyectoById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await proyectoApi.getById(id);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar el proyecto');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crea un nuevo proyecto
   */
  const createProyecto = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await proyectoApi.create(data);
      showSuccessToast('Proyecto creado exitosamente');
      
      // Refrescar la lista de proyectos
      await fetchProyectos();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al crear el proyecto');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchProyectos]);

  /**
   * Actualiza un proyecto existente
   */
  const updateProyecto = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await proyectoApi.update(id, data);
      showSuccessToast('Proyecto actualizado exitosamente');
      
      // Refrescar la lista de proyectos
      await fetchProyectos();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al actualizar el proyecto');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchProyectos]);

  /**
   * Elimina un proyecto
   */
  const deleteProyecto = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await proyectoApi.delete(id);
      showSuccessToast('Proyecto eliminado exitosamente');
      
      // Refrescar la lista de proyectos
      await fetchProyectos();
      
      return true;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al eliminar el proyecto');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchProyectos]);

  /**
   * Obtiene proyectos por usuario
   */
  const fetchProyectosByUser = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await proyectoApi.getByUser(userId);
      return data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar proyectos del usuario');
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Nota: Se eliminó el useEffect automático para evitar bucles infinitos
  // Los componentes deben llamar manualmente fetchProyectos() cuando sea necesario

  return {
    // Estado
    proyectos,
    loading,
    error,
    
    // Funciones
    fetchProyectos,
    fetchProyectoById,
    createProyecto,
    updateProyecto,
    deleteProyecto,
    fetchProyectosByUser,
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useProyectos;
